import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { X, Loader2, AlertTriangle, CheckCircle } from 'lucide-react';

interface MinecraftAccount {
  id: string;
  username: string;
  uuid: string;
  type: 'offline' | 'microsoft';
  accessToken?: string;
  created_at: string;
}

interface MicrosoftLoginModalProps {
  onSuccess: (account: MinecraftAccount) => void;
  onClose: () => void;
}

const MicrosoftLoginModal: React.FC<MicrosoftLoginModalProps> = ({ onSuccess, onClose }) => {
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);

  const handleLogin = async () => {
    if (!window.api) return;
    setStatus('loading');
    setError(null);
    try {
      const result = await window.api.microsoftLogin();
      if (result.success) {
        setStatus('success');
        setTimeout(() => {
          onSuccess(result.account);
        }, 800);
      } else {
        setStatus('error');
        setError(result.error || 'Authentication failed');
      }
    } catch (err: any) {
      setStatus('error');
      setError(err.message || 'An unexpected error occurred');
    }
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-bg-secondary border border-border-color rounded-xl shadow-2xl w-full max-w-md mx-4 p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-highlight border border-border-color flex items-center justify-center">
              <svg viewBox="0 0 21 21" className="w-5 h-5" fill="currentColor">
                <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
                <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
                <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
                <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-bold text-text-primary">Microsoft Account</h2>
              <p className="text-xs text-text-muted">Sign in to play Minecraft</p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={status === 'loading'}
            className="p-1.5 rounded-lg hover:bg-highlight text-text-muted hover:text-text-primary transition-colors disabled:opacity-50"
          >
            <X size={18} />
          </button>
        </div>

        {/* Content */}
        <div className="space-y-4">
          {status === 'idle' && (
            <>
              <p className="text-sm text-text-secondary">
                Connect your Microsoft account to play on any Minecraft server. A sign-in window will open in your browser.
              </p>
              <div className="bg-bg-tertiary border border-border-color rounded-lg p-3 text-xs text-text-muted space-y-1">
                <p>• Your Microsoft credentials are handled by Microsoft directly</p>
                <p>• Only your Minecraft username and UUID are stored locally</p>
                <p>• You can remove the account at any time in Settings</p>
              </div>
            </>
          )}

          {status === 'loading' && (
            <div className="flex flex-col items-center py-4 gap-3">
              <Loader2 className="w-8 h-8 text-gaming-accent animate-spin" />
              <p className="text-sm text-text-secondary">
                Sign-in window opened — complete the login in Microsoft's window.
              </p>
            </div>
          )}

          {status === 'success' && (
            <div className="flex flex-col items-center py-4 gap-3">
              <CheckCircle className="w-8 h-8 text-success" />
              <p className="text-sm text-text-secondary">Successfully signed in!</p>
            </div>
          )}

          {status === 'error' && (
            <div className="bg-bg-tertiary border border-border-color rounded-lg p-3 flex items-start gap-2">
              <AlertTriangle size={16} className="text-error mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm text-text-primary font-medium">Sign-in failed</p>
                <p className="text-xs text-text-muted mt-0.5">{error}</p>
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-3 mt-6">
          {status !== 'loading' && status !== 'success' && (
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2.5 rounded-lg border border-border-color text-sm text-text-secondary hover:text-text-primary hover:bg-highlight transition-colors"
            >
              Cancel
            </button>
          )}
          {(status === 'idle' || status === 'error') && (
            <button
              onClick={handleLogin}
              className="flex-1 px-4 py-2.5 rounded-lg bg-gaming-accent hover:bg-gaming-accent-hover text-white text-sm font-semibold transition-colors flex items-center justify-center gap-2"
            >
              Sign in with Microsoft
            </button>
          )}
          {status === 'loading' && (
            <button
              disabled
              className="flex-1 px-4 py-2.5 rounded-lg bg-highlight text-text-secondary text-sm font-medium flex items-center justify-center gap-2 cursor-not-allowed"
            >
              <Loader2 size={14} className="animate-spin" />
              Signing in...
            </button>
          )}
        </div>
      </div>
    </div>
  , document.body);
};

export default MicrosoftLoginModal;
